import axios, { AxiosInstance } from "axios";
import TokenHelper from "../utils/tokenHelper";
import { generateApiUrlWithQueryParams } from "../utils/query-params";
import {
  EventCommunityBootstrap,
  EventTheme,
  Member,
  RsvpSubmitPayload,
} from "../types";

export type PaginatedAttendees = {
  totalRecords: number;
  records: {
    additionalAttendees: number;
    id: string;
    notification: boolean;
    rsvpStatus: string;
    status: string;
    timestamp: string;
    user: Member;
  }[];
};

export type DonationRecords = {
  totalRecords: number;
  records: {
    id: number;
    timestamp: string;
    isPublic: boolean;
    isAmountPublic: boolean;
    user: {
      email: string;
      lastName: string;
      name: string;
      company: string;
    };
    totalDonated: number;
    hasOrganization: boolean;
    orgName: string;
    comment: string;
  }[];
};

// Params to use for the POST /external-transactions API
export interface CreateExternalTransactionParams {
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    zip?: string | null;
    company?: string; // Company/organization name
    donorAddress?: string | null; // Donor address field
    // IANA time zone from the donor's browser, e.g. "America/Chicago". Lets an
    // export render the donation's timestamps on the donor's own clock.
    timeZone?: string;
  };
  transactions: [
    {
      isAmountPublic: boolean; // Hide my donation amount
      isPublic: boolean; // Hide my name
      product: {
        id?: number; // id from campaign object
        price: string; // the amount the user chose to donate
      };
      description: string | null; // event title
      type: string; // just include this everytime
      quantity: number; // just include this everytime
    },
  ];
  comments: string; // Text Area input
  stripe: string | null; // stripe card token, or null when balance fully covers donation
  flags: {
    fundraiserNotifications: boolean; // optInOption1
    relatedEventNotifications: boolean; // optIntOption2
    event_id: number; // Send the event ID everytime
  };
}

// Params to use for the POST /events/:id/invite/users API
export interface CreateEventInviteNoteParams {
  eventInviteNote: {
    body: string;
    closingSalutation: string;
    hostName: string;
    imagePointer: string;
    transformName: string;
    src: string;
  };
  invitees: {
    email: string;
    firstName?: string;
    lastName?: string;
  }[];
}

// Params to use for the POST /events/:id/thank-you-note API
export interface CreateEventThankYouNoteParams {
  body: string;
  closingSalutation: string;
  hostName: string;
  imagePointer: string;
  users: {
    email: string;
    firstName?: string;
    lastName?: string;
    type?: string;
  }[];
}

// Response from GET /events/:id/users/thank-you-list
export type ThankYouListResponse = {
  totalRecords: number;
  users: {
    id: number;
    firstName: string;
    lastName: string;
    imageId: string;
    imageVersion: string;
    meta: null;
    email: string;
    type: "donated" | "rsvp" | "shared_story";
  }[];
};

export const eventSubjectType = "event";

export const basePath = "/events";
// All the paths for this api domain.
export const eventPaths = {
  triggerRsvpDownload: (eventId: number) =>
    basePath + `/${eventId}/rsvps/download`,

  getById: (eventId: number) => basePath + `/${eventId}`,

  getEventStoriesById: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/stories`,
      queryParams,
    ),

  toggleEventLike: () => "/luvit",

  getDonors: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/donations`,
      queryParams,
    ),

  getAttendees: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/attendees`,
      queryParams,
    ),

  getImages: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/default-images`,
      queryParams,
    ),

  getMyEvents: (queryParams?: string) =>
    generateApiUrlWithQueryParams(basePath + "/my", queryParams),

  createRsvp: (eventId: number) => basePath + `/${eventId}/rsvps`,

  createInviteNote: (eventId: number) => basePath + `/${eventId}/invite/users`,

  createThankYouNote: (eventId: number) =>
    basePath + `/${eventId}/thank-you-note`,

  getThankYouList: (eventId: number) =>
    basePath + `/${eventId}/users/thank-you-list`,

  getCommunityBootstrapById: (eventId: number) =>
    basePath + `/${eventId}/community-bootstrap`,

  createExternalTransaction: () => "/external-transaction",
  eventShareClicks: () => "/event-share-clicks",
};

class EventApi {
  api: AxiosInstance;
  tokenHelper: TokenHelper;

  constructor(api: AxiosInstance, tokenHelper: TokenHelper) {
    this.api = api;
    this.tokenHelper = tokenHelper;
  }

  // Get event by id.
  // GET /events/<id>
  getById = (eventId: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }

    return this.api.get(eventPaths.getById(eventId), {
      headers,
    });
  };

  // Change event location by id.
  // PATCH /events/<id>
  patchLocationById = (eventId: number, newLocation: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        location: {
          name: newLocation,
        },
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change event location by id.
  // PATCH /events/<id>
  patchSettingsById = (eventId: number, newHost: string, status: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        host: newHost,
        visibilityStatus: status,
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change event location by id.
  // PATCH /events/<id>
  patchDeleteEventById = (eventId: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        status: "suspended",
      },
      { headers },
    );
  };

  // Change event name by id.
  // PATCH /events/<id>
  patchNameById = (eventId: number, newTitle: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        name: newTitle,
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change event description by id.
  // PATCH /events/<id>
  patchDescriptionById = (eventId: number, newDescription: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        description: newDescription,
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change fundraising goal by id.
  // PATCH /events/<id>
  patchGoalById = (eventId: number, newGoal: number, campaignId: any) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        campaign: {
          id: campaignId,
          goal: newGoal,
        },
        eventDetailsSaved: true,
      },
      { headers },
    );
  };
  // Change visibility status by id.
  // PATCH /events/<id>
  patchVisibilityById = (eventId: number, status: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        visibilityStatus: status,
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change event date and time by id.
  // PATCH /events/<id>
  patchTimeById = (
    eventId: number,
    allDay: boolean,
    startAt: string,
    endAt: string,
    timeZone: string,
  ) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        allDay: allDay,
        startAt: startAt,
        endAt: endAt,
        timeZone: timeZone,
        eventDetailsSaved: true,
      },
      { headers },
    );
  };

  // Change event date and time by id.
  // PATCH /events/<id>
  patchThemeById = (eventId: number, theme: EventTheme) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.patch(
      eventPaths.getById(eventId),
      {
        images: [theme],
      },
      { headers },
    );
  };

  // Get event's stories
  // GET /events/<id>/stories
  getEventStoriesById = (eventId: number, queryParams?: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }

    return this.api.get(eventPaths.getEventStoriesById(eventId, queryParams), {
      headers,
    });
  };

  // Get event's donations
  // GET /events/<id>/donations
  getDonors = (eventId: number, queryParams?: string) => {
    return this.api.get<DonationRecords>(
      eventPaths.getDonors(eventId, queryParams),
    );
  };

  // Get event's members (attendees)
  // GET /events/<id>/attendees
  getAttendees = (eventId: number, queryParams?: string) => {
    return this.api.get<PaginatedAttendees>(
      eventPaths.getAttendees(eventId, queryParams),
    );
  };

  // Get all image options for an event
  // GET /events/<id>/default-images
  getImages = (eventId: number, queryParams?: string) => {
    return this.api.get<any>(eventPaths.getImages(eventId, queryParams));
  };

  // Toggle event like.
  // POST /luvit
  toggleEventLike = (eventId: number) => {
    const token = this.tokenHelper.getRefreshToken();
    return this.api.post(
      eventPaths.toggleEventLike(),
      {
        subjectType: eventSubjectType,
        subjectId: eventId,
      },
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      },
    );
  };

  // Sends a email to the event owner with the rsvp list.
  // GET /events/<id>/rsvp/download
  triggerRsvpDownload = (eventId: number) => {
    const token = this.tokenHelper.getRefreshToken();

    return this.api.get(eventPaths.triggerRsvpDownload(eventId), {
      headers: {
        Authorization: this.tokenHelper.formatAuthHeader(token),
      },
    });
  };

  // Get the logged-in user's events (owned + RSVP'd)
  // GET /events/my
  getMyEvents = (queryParams?: string) => {
    const token = this.tokenHelper.getJwtToken();
    let headers: Record<string, any> = {};
    if (token) {
      headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
    }
    return this.api.get(eventPaths.getMyEvents(queryParams), { headers });
  };

  // Submit an RSVP for an event
  // POST /events/:id/rsvps
  createRsvp = (eventId: number, payload: RsvpSubmitPayload) => {
    const token = this.tokenHelper.getJwtToken();
    let headers: Record<string, any> = {};
    if (token) {
      headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
    }
    return this.api.post(eventPaths.createRsvp(eventId), payload, { headers });
  };

  // Create an Event Invite Note
  // POST /events/:id/invite/users
  createInviteNote = (
    eventId: number,
    inviteNoteParams: CreateEventInviteNoteParams,
  ) => {
    const token = this.tokenHelper.getRefreshToken();

    return this.api.post(
      eventPaths.createInviteNote(eventId),
      inviteNoteParams,
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      },
    );
  };

  // Activate or decline fundraiser activation.
  // PATCH /events/<id>
  private patchFundraiserStatus = (
    eventId: number,
    changedToFundraiser: boolean,
    productId?: string,
  ) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    const payload: Record<string, any> = {
      changed_to_fundraiser: changedToFundraiser,
    };
    if (productId) {
      payload.product_id = productId;
    }
    return this.api.patch(eventPaths.getById(eventId), payload, { headers });
  };

  patchActivateFundraiser = (eventId: number, productId?: string) =>
    this.patchFundraiserStatus(eventId, true, productId);

  patchDeclineFundraiser = (eventId: number, productId?: string) =>
    this.patchFundraiserStatus(eventId, false, productId);

  // Unsubscribe from event notification emails.
  unsubscribe = (eventId: number, eventAttendeeId: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();
      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }
    return this.api.put(
      `/events/${eventId}/unsubscribe/${eventAttendeeId}`,
      "",
      { headers },
    );
  };

  // Create an Donation Transaction
  // POST /external-transactions
  createExternalTransaction = (
    donationParams: CreateExternalTransactionParams,
  ) => {
    return this.api.post(
      eventPaths.createExternalTransaction(),
      donationParams,
    );
  };

  // Track social share button click.
  // POST /event-share-clicks
  trackSocialShare = (eventId: number, shareType: string) => {
    return this.api.post(eventPaths.eventShareClicks(), {
      eventId,
      shareType,
    });
  };

  // Send a Thank You Note (admin-only, uses refresh token)
  // POST /events/:id/thank-you-note
  createThankYouNote = (
    eventId: number,
    thankYouNoteParams: CreateEventThankYouNoteParams,
  ) => {
    const token = this.tokenHelper.getRefreshToken();
    let headers: Record<string, any> = {};
    if (token) {
      headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
    }

    return this.api.post(
      eventPaths.createThankYouNote(eventId),
      thankYouNoteParams,
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      },
    );
  };

  // Get the list of users eligible for thank you notes (admin-only, uses refresh token)
  // GET /events/:id/users/thank-you-list
  getThankYouList = (eventId: number) => {
    const token = this.tokenHelper.getRefreshToken();
    let headers: Record<string, any> = {};
    if (token) {
      headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
    }

    return this.api.get<ThankYouListResponse>(
      eventPaths.getThankYouList(eventId),
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      },
    );
  };

  // Get the event's community and org boostrap
  // GET /events/:id/community-boostrap
  getCommunityBootstrapById = (eventId: number) => {
    return this.api.get<EventCommunityBootstrap>(
      eventPaths.getCommunityBootstrapById(eventId),
    );
  };
}

export default EventApi;
